import type { Account, AccountKind, Plan } from '../plan/model';

/**
 * Contribution & withdrawal ordering strategies (PLAN.md §2a). The simulator funnels each
 * year's surplus into accounts in CONTRIBUTION order, and draws each year's shortfall from
 * accounts in WITHDRAWAL order. The Thai life-cycle ordering PLAN.md describes is:
 *   - contributions: PVD employer match -> RMF / Thai ESG / SSF up to the deduction cap
 *                    -> taxable / cash.
 *   - withdrawals:   taxable / cash -> retirement vehicles (RMF only AFTER age 55) -> ...
 *
 * What is M2 vs M4:
 *  - M2 (here): the pluggable interface + a PURE, life-cycle-aware default ORDERING. Ordering
 *    is structural — it sorts account ids by tax-vehicle bucket and gates RMF withdrawals on
 *    the age-55 rule, all derivable from the plan with NO tax law inlined.
 *  - M4: the deduction CAPS (RMF ~30% of income / ~THB 500k combined, SSF/Thai ESG caps) and
 *    the early-withdrawal CLAWBACK/penalty. Those are dollar amounts the TaxPolicy owns; this
 *    file leaves them as typed hooks/TODOs and never computes a baht of tax itself.
 *
 * Note: the serialized `Strategy` type (from plan/model) is the plan-level DECLARATION of
 * order; the runtime `OrderingStrategy` interface here is the pluggable BEHAVIOUR the
 * simulator calls. `DefaultOrdering` reads the declaration first (explicit user intent wins);
 * `ThaiLifecycleOrdering` synthesises the PLAN.md life-cycle order when the plan leaves the
 * lists empty.
 */

// ---------------------------------------------------------------------------
// Runtime ordering interface (the simulator's contract)
// ---------------------------------------------------------------------------

/**
 * The runtime ordering strategy the simulator consumes. Two methods, each returning account
 * ids in priority order for the given simulated `year` (so a strategy can change ordering
 * across the life-cycle, e.g. unlock RMF withdrawals at age 55).
 *
 * THIS IS THE PUBLISHED SIGNATURE for the M2 Build agents:
 *   contributionOrder(plan: Plan, year: number): string[]   // surplus routed in this order
 *   withdrawalOrder(plan: Plan, year: number): string[]      // shortfall drawn in this order
 *
 * Both methods are PURE: same `(plan, year)` -> same array, no I/O, no randomness. They return
 * a SUBSET-or-full list of `plan.accounts` ids; ids an ordering deliberately excludes for the
 * year (e.g. a locked RMF in a withdrawal context) are simply omitted, so the simulator skips
 * them. Every returned id is guaranteed to be a real account on the plan.
 */
export interface OrderingStrategy {
  /** Account ids, highest priority first, for routing surplus this `year`. */
  contributionOrder(plan: Plan, year: number): string[];
  /** Account ids, first-to-draw first, for covering a shortfall this `year`. */
  withdrawalOrder(plan: Plan, year: number): string[];
}

/**
 * @deprecated split half of the legacy M0 interface. Prefer `OrderingStrategy`.
 * Resolves the order in which accounts receive contributions in a given year.
 */
export interface ContributionStrategy {
  /** Account ids, highest priority first, for routing surplus this `year`. */
  order(plan: Plan, year: number): string[];
}

/**
 * @deprecated split half of the legacy M0 interface. Prefer `OrderingStrategy`.
 * Resolves the order in which accounts are drawn down in a given year.
 */
export interface WithdrawalStrategy {
  /** Account ids, first-to-draw first, for covering a shortfall this `year`. */
  order(plan: Plan, year: number): string[];
}

// ---------------------------------------------------------------------------
// Age & RMF-gate helpers (PURE — derived from the plan, no tax law)
// ---------------------------------------------------------------------------

/** The Thai RMF withdrawal-eligibility age. PLAN.md §2a: "RMF after 55". */
export const RMF_WITHDRAWAL_AGE = 55;

/** Ages of every household member during calendar `year` (age = year - birthYear). */
export function householdAges(plan: Plan, year: number): number[] {
  return plan.household.people.map((p) => year - p.birthYear);
}

/**
 * Whether RMF money may be WITHDRAWN in calendar `year` without triggering the early-exit
 * penalty — i.e. some household member has reached `RMF_WITHDRAWAL_AGE`. This is the ORDERING
 * gate only: it decides whether RMF is *offered* as a withdrawal source. The actual
 * clawback/penalty math on an under-55 withdrawal is a TaxPolicy concern (M4 — see
 * `WithdrawalClawbackHook`), not computed here.
 *
 * NOTE: the "AND held >= 5 years" leg of the RMF rule and per-account ownership (which
 * member's age gates which RMF) are M4 refinements; M2 gates on the household reaching 55,
 * which is the dominant case for a single/joint plan.
 */
export function rmfWithdrawalUnlocked(plan: Plan, year: number): boolean {
  return householdAges(plan, year).some((age) => age >= RMF_WITHDRAWAL_AGE);
}

// ---------------------------------------------------------------------------
// Tax-vehicle bucketing (structural priority, NOT tax computation)
// ---------------------------------------------------------------------------

/**
 * Contribution priority by account kind (lower number = funded first), following PLAN.md's
 * "PVD employer match -> RMF/Thai ESG/SSF up to the deduction cap -> taxable" ladder. This is
 * a pure ORDERING; the deduction CAP that limits how much actually flows into each tax-
 * advantaged bucket is an M4 TaxPolicy concern (see `ContributionCapHooks`).
 */
const CONTRIBUTION_RANK: Record<AccountKind, number> = {
  // 1. Capture the employer match first — it is "free money" with the highest priority.
  pvd: 0,
  // 2. Tax-advantaged retirement/ESG vehicles, up to their (M4) deduction caps.
  rmf: 1,
  thai_esg: 1,
  ssf: 1,
  // 2b. US (M8) tax-advantaged retirement vehicles — fund alongside the Thai retirement bucket.
  //     (A US-only plan never has Thai kinds present, so the relative order is what matters.)
  traditional: 1,
  roth: 1,
  // 3. Ordinary taxable sleeves and cash mop up the remainder.
  taxable_set: 2,
  taxable_foreign: 2,
  taxable_us: 2,
  cash: 3,
};

/**
 * Withdrawal priority by account kind (lower number = drawn first), following PLAN.md's
 * "taxable -> RMF after 55 -> ..." ladder. Draw liquid/already-taxed money first and leave
 * tax-advantaged retirement money to compound. RMF is gated separately on age (see
 * `rmfWithdrawalUnlocked`) — this rank only orders it once it is eligible.
 */
const WITHDRAWAL_RANK: Record<AccountKind, number> = {
  // 1. Cash and taxable sleeves first (SET-listed gains are exempt; foreign sleeve handled by
  //    the remittance rule in M4 — ordering is the same here). The US taxable brokerage draws here
  //    too (already-taxed principal + preferential-rate gains).
  cash: 0,
  taxable_set: 1,
  taxable_foreign: 1,
  taxable_us: 1,
  // 2. SSF / Thai ESG next.
  ssf: 2,
  thai_esg: 2,
  // 2b. US (M8) Roth before traditional — Roth qualified withdrawals are tax-free, so drawing the
  //     deepest tax-deferred (traditional) money last lets it keep compounding (mirrors the Thai
  //     "leave the deepest retirement money to last" intent). Note: RMDs may force traditional
  //     income at 73+ regardless of ordering — that floor is the TaxPolicy's job, not ordering.
  roth: 2,
  // 3. RMF, PVD, and the US traditional sleeve (the deepest retirement money) last.
  rmf: 3,
  pvd: 3,
  traditional: 3,
};

/** Stable bucket sort of accounts by a rank table, preserving natural order within a bucket. */
function rankedOrder(accounts: readonly Account[], rank: Record<AccountKind, number>): string[] {
  return accounts
    .map((a, index) => ({ id: a.id, key: rank[a.kind], index }))
    .sort((x, y) => x.key - y.key || x.index - y.index)
    .map((e) => e.id);
}

function naturalOrder(accounts: readonly Account[]): string[] {
  return accounts.map((a) => a.id);
}

// ---------------------------------------------------------------------------
// M4 hooks — typed placeholders for the tax-driven caps/clawbacks (NOT implemented here)
// ---------------------------------------------------------------------------

/**
 * M4 HOOK (not implemented in M2). Per-account contribution CAP for `year` — how much of a
 * routed surplus may actually land in a tax-advantaged bucket before the deduction cap
 * (RMF ~30% of income / ~THB 500k combined, SSF/Thai ESG caps) bites. Returning `undefined`
 * means "no cap" (current M2 behaviour: the whole surplus may flow). When M4 wires this, the
 * cap is supplied by the TaxPolicy, never computed here — the strategy stays tax-law-free.
 */
export type ContributionCapHook = (
  plan: Plan,
  accountId: string,
  year: number,
) => number | undefined;

/**
 * M4 HOOK (not implemented in M2). Whether withdrawing from `accountId` in `year` would
 * trigger an RMF/SSF early-exit clawback + penalty (TaxPolicy decides the baht; this only
 * reports whether the simulator should avoid/penalise the draw). M2 expresses the same intent
 * structurally via `rmfWithdrawalUnlocked` gating RMF out of the withdrawal order before 55.
 */
export type WithdrawalClawbackHook = (plan: Plan, accountId: string, year: number) => boolean;

/** Optional M4 cap/clawback hooks an ordering may consult. All default to "no effect" in M2. */
export interface OrderingHooks {
  readonly contributionCap?: ContributionCapHook;
  readonly withdrawalClawback?: WithdrawalClawbackHook;
}

// ---------------------------------------------------------------------------
// Concrete strategies
// ---------------------------------------------------------------------------

/**
 * Default: honour whatever explicit order the plan's `strategy` declares, falling back to
 * the natural account order when the plan leaves a list empty. No tax-aware reordering —
 * pure pass-through of the serialized declaration. This is the conservative default the
 * engine ships with so a plan's explicit intent is never silently overridden.
 *
 * Implements the runtime `OrderingStrategy` (and the legacy split interfaces). Explicit
 * order lists are filtered to ids that actually exist on the plan, so a stale id can never
 * be handed back to the simulator.
 */
export class DefaultOrdering implements OrderingStrategy, ContributionStrategy, WithdrawalStrategy {
  /** Legacy combined accessor: returns the natural account order. */
  order(plan: Plan, _year: number): string[] {
    return naturalOrder(plan.accounts);
  }

  contributionOrder(plan: Plan, _year: number): string[] {
    const declared = filterToAccounts(plan, plan.strategy.contributionOrder);
    return declared.length > 0 ? declared : naturalOrder(plan.accounts);
  }

  withdrawalOrder(plan: Plan, _year: number): string[] {
    const declared = filterToAccounts(plan, plan.strategy.withdrawalOrder);
    return declared.length > 0 ? declared : naturalOrder(plan.accounts);
  }
}

/**
 * Thai life-cycle ordering (PLAN.md §2a). When the plan DOES NOT declare an explicit order,
 * this synthesises the canonical Thai ladder:
 *   contributions: PVD (employer match) -> RMF / Thai ESG / SSF -> taxable -> cash.
 *   withdrawals:   cash -> taxable -> SSF / Thai ESG -> RMF (only after age 55) -> PVD.
 *
 * It is PURE and life-cycle-aware: `withdrawalOrder` OMITS RMF accounts until a household
 * member reaches `RMF_WITHDRAWAL_AGE`, so the simulator never draws RMF early in the ordinary
 * case (the early-withdrawal clawback/penalty itself is M4 TaxPolicy work — see hooks).
 * An explicit plan declaration always wins (user intent overrides the heuristic ladder).
 *
 * The M4 cap/clawback hooks are accepted but UNUSED in M2 — wiring them is M4's job; carrying
 * the field keeps the constructor signature stable across milestones.
 */
export class ThaiLifecycleOrdering
  implements OrderingStrategy, ContributionStrategy, WithdrawalStrategy
{
  /** M4 cap/clawback hooks (currently unused — see `OrderingHooks`). */
  readonly hooks: OrderingHooks;

  constructor(hooks: OrderingHooks = {}) {
    this.hooks = hooks;
  }

  /** Legacy combined accessor: contributions are the more common "order" intent. */
  order(plan: Plan, year: number): string[] {
    return this.contributionOrder(plan, year);
  }

  contributionOrder(plan: Plan, _year: number): string[] {
    const declared = filterToAccounts(plan, plan.strategy.contributionOrder);
    if (declared.length > 0) {
      return declared;
    }
    // No explicit declaration -> synthesise the PLAN.md contribution ladder.
    // TODO(M4): cap each tax-advantaged bucket via `hooks.contributionCap` before overflow
    // spills to the next bucket; M2 only fixes the ORDER, not the amounts.
    return rankedOrder(plan.accounts, CONTRIBUTION_RANK);
  }

  withdrawalOrder(plan: Plan, year: number): string[] {
    const declared = filterToAccounts(plan, plan.strategy.withdrawalOrder);
    if (declared.length > 0) {
      return declared;
    }
    // No explicit declaration -> synthesise the PLAN.md withdrawal ladder, gating RMF on the
    // age-55 rule. Drawing locked RMF early is avoided structurally (it is omitted here);
    // TODO(M4): if a forced early RMF draw is ever unavoidable, `hooks.withdrawalClawback`
    // tells the TaxPolicy to assess the clawback + penalty — never computed in this file.
    const rmfUnlocked = rmfWithdrawalUnlocked(plan, year);
    const eligible = rmfUnlocked ? plan.accounts : plan.accounts.filter((a) => a.kind !== 'rmf');
    return rankedOrder(eligible, WITHDRAWAL_RANK);
  }
}

/** Keep only the ids that correspond to a real account on the plan, preserving caller order. */
function filterToAccounts(plan: Plan, ids: readonly string[]): string[] {
  const known = new Set(plan.accounts.map((a) => a.id));
  return ids.filter((id) => known.has(id));
}

/** The strategy the engine uses unless a plan provides its own. */
export const defaultOrdering: DefaultOrdering = new DefaultOrdering();

/** The Thai life-cycle ordering, ready to plug into the simulator when no explicit order is set. */
export const thaiLifecycleOrdering: ThaiLifecycleOrdering = new ThaiLifecycleOrdering();
